import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Spin } from "antd";
import socket from "../Sales1/socket";
import { SERVER_URL } from "../../../databucket";

// Status badge config — shared across Sales Outlook reading pane + Lead's Email Outreach cards
const STATUS_CONFIG = {
  sent: { color: "#605e5c", bg: "#f3f3f3", label: "Sent", dot: "#605e5c", icon: "📤" },
  delivered: { color: "#107c10", bg: "#dff6dd", label: "Delivered", dot: "#107c10", icon: "✅" },
  opened: { color: "#0078d4", bg: "#deecf9", label: "Opened", dot: "#0078d4", icon: "👁" },
  bounced: { color: "#c50f1f", bg: "#fde7e9", label: "Bounced", dot: "#c50f1f", icon: "↩️" },
  clicked: { color: "#7f439c", bg: "#f0e8ff", label: "Clicked", dot: "#7f439c", icon: "🔗" },
  replied: { color: "#107c10", bg: "#dff6dd", label: "Replied", dot: "#107c10", icon: "↩️" },
  complained: { color: "#d13438", bg: "#fde7e9", label: "Complained", dot: "#d13438", icon: "⚠️" },
};

function uaToDevice(ua = "") {
  if (!ua) return { icon: "💻", label: "Unknown device" };
  const u = ua.toLowerCase();
  if (u.includes("iphone") || (u.includes("android") && u.includes("mobile"))) return { icon: "📱", label: "Mobile" };
  if (u.includes("ipad") || u.includes("tablet")) return { icon: "📱", label: "Tablet" };
  if (u.includes("mac")) return { icon: "🍎", label: "Mac" };
  if (u.includes("windows")) return { icon: "🪟", label: "Windows" };
  return { icon: "💻", label: "Desktop" };
}

/**
 * Renders a collapsible tracking timeline for a SES-tracked email.
 * Props:
 *   - email: { trackId?, id?, messageId? } — at least one identifier is required
 *   - defaultOpen?: boolean (default false) — expand timeline on first render
 */
const TrackingPanel = ({ email, defaultOpen = false }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(defaultOpen);

  const load = useCallback(() => {
    const q = email?.trackId
      ? `trackId=${encodeURIComponent(email.trackId)}`
      : `messageId=${encodeURIComponent(email?.id || email?.messageId || "")}`;
    setLoading(true);
    axios
      .get(`${SERVER_URL}/api/ses/tracking?${q}`)
      .then((r) => setTracking(r.data))
      .catch(() => setTracking(null))
      .finally(() => setLoading(false));
  }, [email?.id, email?.messageId, email?.trackId]);

  useEffect(() => {
    setTracking(null);
    load();
  }, [load]);

  // Live updates when a socket event arrives for this email
  useEffect(() => {
    const handler = (evt) => {
      if (
        evt.trackId === email?.trackId ||
        evt.messageId === (email?.messageId || email?.id)
      ) {
        load();
      }
    };
    socket.on("email:sesEvent", handler);
    return () => socket.off("email:sesEvent", handler);
  }, [email?.trackId, email?.messageId, email?.id, load]);

  if (loading) {
    return (
      <div style={{ padding: "8px 16px", background: "#faf9f8", borderTop: "1px solid #edebe9", fontSize: 12, color: "#a19f9d", display: "flex", alignItems: "center", gap: 8 }}>
        <Spin size="small" /> Loading tracking...
      </div>
    );
  }
  if (!tracking) return null;

  const cfg = STATUS_CONFIG[tracking.status] || STATUS_CONFIG.sent;

  // Build a chronological timeline of all events
  const timeline = [];
  if (tracking.sentAt) timeline.push({ type: "sent", at: tracking.sentAt });
  if (tracking.deliveredAt) timeline.push({ type: "delivered", at: tracking.deliveredAt });
  (tracking.opens || []).forEach((o) =>
    timeline.push({
      type: "opened",
      at: o.at,
      city: o.city,
      region: o.region,
      country: o.country,
      userAgent: o.userAgent,
      ip: o.ip,
    })
  );
  (tracking.clicks || []).forEach((c) =>
    timeline.push({ type: "clicked", at: c.at, url: c.url, ip: c.ip })
  );
  if (tracking.bouncedAt)
    timeline.push({
      type: tracking.status === "complained" ? "complained" : "bounced",
      at: tracking.bouncedAt,
      reason: tracking.bounceReason,
    });
  if (tracking.repliedAt) timeline.push({ type: "replied", at: tracking.repliedAt });
  timeline.sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div style={{ background: "#faf9f8", borderTop: "1px solid #edebe9", fontSize: 12 }}>
      {/* Header row */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
      >
        <span style={{ fontWeight: 700, color: "#323130", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.6px" }}>
          📊 Tracking
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "2px 10px",
            borderRadius: 10,
            background: cfg.bg,
            color: cfg.color,
            fontWeight: 700,
            fontSize: 11,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
          {cfg.label}
        </span>
        {tracking.openCount > 0 && (
          <span style={{ color: "#0078d4", fontWeight: 600 }}>
            👁 {tracking.openCount} open{tracking.openCount > 1 ? "s" : ""}
          </span>
        )}
        {tracking.clickCount > 0 && (
          <span style={{ color: "#7f439c", fontWeight: 600 }}>
            🔗 {tracking.clickCount} click{tracking.clickCount > 1 ? "s" : ""}
          </span>
        )}
        <span style={{ marginLeft: "auto", color: "#a19f9d", fontSize: 11 }}>{open ? "▲" : "▼"}</span>
      </div>

      {/* Timeline */}
      {open && (
        <div style={{ padding: "0 16px 12px 20px", display: "flex", flexDirection: "column", gap: 0, maxHeight: 280, overflowY: "auto" }}>
          {timeline.map((evt, i) => {
            const ecfg = STATUS_CONFIG[evt.type] || STATUS_CONFIG.sent;
            const dev = evt.userAgent ? uaToDevice(evt.userAgent) : null;
            const isLast = i === timeline.length - 1;
            return (
              <div key={i} style={{ display: "flex", gap: 12, position: "relative" }}>
                {!isLast && <div style={{ position: "absolute", left: 7, top: 20, bottom: 0, width: 2, background: "#edebe9" }} />}
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: ecfg.dot, flexShrink: 0, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", zIndex: 1 }}>
                  {ecfg.icon}
                </div>
                <div style={{ paddingBottom: 10, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: "#323130", fontSize: 12 }}>
                    {ecfg.label}
                    <span style={{ fontWeight: 400, color: "#797775", marginLeft: 8 }}>
                      {dayjs(evt.at).format("MMM D, YYYY · h:mm A")}
                    </span>
                  </div>
                  {evt.city && (
                    <div style={{ color: "#605e5c", marginTop: 2 }}>
                      📍 {[evt.city, evt.region, evt.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {dev && (
                    <div style={{ color: "#605e5c", marginTop: 1 }}>
                      {dev.icon} {dev.label}
                    </div>
                  )}
                  {evt.url && (
                    <div style={{ color: "#7f439c", marginTop: 2, wordBreak: "break-all" }}>
                      🔗 {evt.url}
                    </div>
                  )}
                  {evt.reason && (
                    <div style={{ color: "#c50f1f", marginTop: 2 }}>
                      ❌ {evt.reason}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrackingPanel;
